import { MapPin, Square, Euro } from "lucide-react";
import type { ToolWidgetProps } from "./index";
import { ProfileCardActions } from "./profile-card-actions";

interface Address {
  postal_code?: string;
  locality?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  administrative_area_level_1?: string;
  country?: string;
}

interface Location {
  id: number;
  name: string;
  user_id?: number;
  surface?: number;
  price?: number;
  chalandise?: number;
  walls_for_sale?: boolean;
  business_for_sale?: boolean;
  lease_for_sale?: boolean;
  popup_available?: boolean;
  address?: Address;
}

interface SearchLocationsData {
  results: Location[];
  total: number;
}

function LocationCard({ location }: { location: Location }) {
  return (
    <div className="flex flex-col gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors relative">
      <ProfileCardActions
        profile={{
          type: "location",
          id: location.id,
          name: location.name,
          user_id: location.user_id,
        }}
      />
          <div className="w-16 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-slate-400" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-slate-800 truncate">
              {location.name}
            </div>
            {location.address?.formatted_address && (
              <div className="text-xs text-slate-500 truncate">
                {location.address.formatted_address}
              </div>
            )}
            <div className="flex flex-col gap-1 mt-2 text-xs text-slate-500">
              {location.surface && (
                <div className="flex items-center gap-1">
                  <Square className="w-3 h-3" />
                  {location.surface} m²
                </div>
              )}
              {location.price && (
                <div className="flex items-center gap-1">
                  <Euro className="w-3 h-3" />
                  {location.price.toLocaleString("fr-FR")} €
                </div>
              )}
            </div>
          </div>
    </div>
  );
}

export function SearchLocationsWidget({
  data,
  count,
}: ToolWidgetProps<SearchLocationsData>) {
  const { results = [] } = data || {};

  if (!results.length) {
    return (
      <div className="text-sm text-slate-500 italic">Aucun local trouvé</div>
    );
  }

  return (
    <div className="grid grid-cols-4 gap-3">
      {results.map((location) => (
        <LocationCard key={location.id} location={location} />
      ))}
      {count && count > results.length && (
        <div className="col-span-4 text-xs text-slate-400 text-center pt-1">
          + {count - results.length} autres résultats
        </div>
      )}
    </div>
  );
}
